/**
 * PR-D4-046 / PR-D4-050 — Durable full-session review (centered, no SatTest chrome).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SessionReviewCard } from '../components/SessionReviewCard';
import { api, ApiError } from '../lib/api-client';
import { resolveTelemetryUserId } from '../lib/auth-session';
import type { Question, SessionReviewPayload } from '../lib/types';
import './ReviewSessionPage.css';

export function ReviewSessionPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get('sessionId') ?? '';
    const userId = resolveTelemetryUserId();
    const [payload, setPayload] = useState<SessionReviewPayload | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!sessionId) {
            setError('Missing sessionId');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        void api
            .getSessionFull(sessionId, userId)
            .then((data) => {
                if (!cancelled) {
                    setPayload(data);
                    setError('');
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setPayload(null);
                    const message =
                        err instanceof ApiError
                            ? `${err.message} (${err.code}, HTTP ${err.status})`
                            : err instanceof Error
                              ? err.message
                              : 'Failed to load session';
                    console.error('[review-session] Failed to load session', {
                        sessionId,
                        userId,
                        error: err,
                    });
                    setError(message);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId, userId]);

    if (loading) {
        return (
            <div className="review-session-page">
                <div className="review-session-page__hero-inner">
                    <p className="review-session-page__subtitle">Loading session…</p>
                </div>
            </div>
        );
    }

    if (error || !payload) {
        return (
            <div className="review-session-page">
                <div className="review-session-page__hero-inner">
                    <h1 className="review-session-page__title">Could not open session review</h1>
                    <p className="review-session-page__subtitle">{error || 'Session not found.'}</p>
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const questions = payload.questions as Question[];
    const score = payload.session.score;
    const maxScore = payload.session.maxScore;
    const handleDelete = async () => {
        if (deleting || !window.confirm('Удалить эту сессию из истории?')) return;
        setDeleting(true);
        try {
            await api.deleteSession(payload.session.id);
            navigate('/dashboard');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete session';
            console.error('[review-session] Failed to delete session', {
                sessionId: payload.session.id,
                error: err,
            });
            setError(message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="review-session-page">
            <header className="review-session-page__hero">
                <div className="review-session-page__hero-inner">
                    <p className="review-session-page__brand">SAT Generator</p>
                    <h1 className="review-session-page__title">Full Session Review</h1>
                    <p className="review-session-page__subtitle">
                        {payload.session.moduleType} · {payload.session.mode}
                        {score != null && maxScore != null ? ` · score ${score}/${maxScore}` : ''}
                        {` · ${questions.length} questions`}
                    </p>
                    <button
                        type="button"
                        className="review-session-page__back"
                        onClick={() => navigate('/dashboard')}
                    >
                        Back to Dashboard
                    </button>
                    <button
                        type="button"
                        className="review-session-page__delete"
                        onClick={() => void handleDelete()}
                        disabled={deleting}
                    >
                        {deleting ? 'Удаление…' : 'Удалить сессию'}
                    </button>
                    {error ? <p className="review-session-page__error">{error}</p> : null}
                </div>
            </header>

            <div className="review-session-page__list">
                {questions.length === 0 ? (
                    <p className="review-session-page__subtitle review-session-page__list-heading">No questions found for this session.</p>
                ) : (
                    questions.map((question, index) => {
                        const result = payload.results[index];
                        return (
                            <SessionReviewCard
                                key={`${question.id}-${index}`}
                                question={question}
                                questionNumber={index + 1}
                                totalQuestions={questions.length}
                                selectedOption={result?.selectedOption ?? null}
                                isCorrect={Boolean(result?.isCorrect)}
                                timeSpentSeconds={result?.timeSpentSeconds}
                            />
                        );
                    })
                )}
            </div>
        </div>
    );
}
